<!-- src/views/legal/TermsOfService.vue -->
<!-- TEMPLATE — have counsel review before production reliance. -->

<script setup lang="ts">
  import { RouterLink } from 'vue-router';

  // Fixed publication date. Do NOT compute at runtime (no Date.now) so the
  // "Last updated" string is stable and auditable across renders/deploys.
  const lastUpdated = 'July 6, 2026';

  const h2 = 'mt-12 mb-3 text-xl font-semibold text-gray-900 dark:text-gray-100';
  const p = 'mb-4 text-base leading-7 text-gray-600 dark:text-gray-300';
  const ul = 'mb-4 ml-5 list-disc space-y-2 text-base leading-7 text-gray-600 dark:text-gray-300';
  const a =
    'font-medium text-brand-600 underline underline-offset-2 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300';
</script>

<template>
  <div class="container mx-auto max-w-3xl px-4 py-8">
    <article aria-labelledby="terms-title">
      <header>
        <h1
          id="terms-title"
          class="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
          Terms of Service
        </h1>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Last updated: {{ lastUpdated }}
        </p>
      </header>

      <p :class="[p, 'mt-8']">
        These terms govern your use of BurnLink, a service for sharing information through links
        that self-destruct after a single view or on expiry. By creating, sending, or viewing a
        secret, you agree to these terms. If you do not agree, do not use the service.
      </p>

      <h2 :class="h2">Acceptable use</h2>
      <p :class="p">
        You are responsible for the content you share and for how you use the links you create. You
        agree not to use BurnLink to:
      </p>
      <ul :class="ul">
        <li>Share, store, or transmit anything illegal, or that infringes others' rights.</li>
        <li>Distribute malware, phishing content, or links intended to defraud or deceive.</li>
        <li>Harass, threaten, or harm others, or facilitate abuse.</li>
        <li>Attempt to breach, overload, probe, or disrupt the service or its infrastructure.</li>
        <li>Circumvent rate limits, authentication, or other technical or security controls.</li>
        <li>Violate any applicable law or regulation.</li>
      </ul>
      <p :class="p">
        Because the content of secrets is encrypted and unreadable to us, we rely on you to use the
        service lawfully. We may suspend or block access, and remove stored ciphertext, where we
        reasonably believe these terms are being violated.
      </p>

      <h2 :class="h2">How one-time links behave</h2>
      <p :class="p">
        A secret is designed to be viewable exactly once, or until it expires, and is then
        permanently destroyed. You acknowledge and accept the consequences of this design:
      </p>
      <ul :class="ul">
        <li>
          Once a link is opened, or once its expiration passes, the secret is gone and
          <strong class="font-semibold text-gray-800 dark:text-gray-200">
            cannot be recovered</strong> — by you or by us.
        </li>
        <li>
          Anyone who opens the link first will see the content; sharing a link over an insecure
          channel may allow an unintended recipient to consume it.
        </li>
        <li>
          Link previews, security scanners, or messaging clients that automatically fetch URLs may
          open a link and thereby burn it. Share links thoughtfully.
        </li>
      </ul>

      <h2 :class="h2">Accounts</h2>
      <p :class="p">
        Some features require an account. You are responsible for maintaining the confidentiality of
        your credentials and for activity under your account. Notify us promptly of any unauthorized
        use.
      </p>

      <h2 :class="h2">Service provided "as is"</h2>
      <p :class="p">
        BurnLink is provided
        <strong class="font-semibold text-gray-800 dark:text-gray-200">"as is" and "as
        available," without warranties of any kind</strong>, whether express or implied, including
        but not limited to warranties of merchantability, fitness for a particular purpose,
        availability, or non-infringement. We do not warrant that the service will be uninterrupted,
        error-free, or that any secret will be delivered to, or viewed by, a particular recipient.
      </p>

      <h2 :class="h2">Limitation of liability</h2>
      <p :class="p">
        To the maximum extent permitted by law, BurnLink and its operators will not be liable for
        any indirect, incidental, special, consequential, or punitive damages, or for any loss of
        data, profits, or goodwill, arising out of or related to your use of (or inability to use)
        the service — including any premature disclosure, non-delivery, loss, or destruction of a
        secret. You use the service at your own risk and are responsible for evaluating whether it
        meets your security and compliance needs.
      </p>

      <h2 :class="h2">Underlying project</h2>
      <p :class="p">
        BurnLink is based on the MIT-licensed Onetime Secret project. BurnLink's hosted
        service, branding, and product changes are operated separately.
      </p>

      <h2 :class="h2">Changes and termination</h2>
      <p :class="p">
        We may modify these terms or the service at any time. Material changes to these terms will
        be reflected in the "Last updated" date above; continued use after a change constitutes
        acceptance. We may suspend or terminate access at our discretion, including for violations
        of these terms. You may stop using the service, and delete your account, at any time.
      </p>

      <h2 :class="h2">Contact</h2>
      <p :class="p">
        Questions about these terms? Email
        <a :class="a" href="mailto:support@burnlink.app">support@burnlink.app</a>. See also our
        <RouterLink :class="a" to="/privacy">Privacy Policy</RouterLink> and
        <RouterLink :class="a" to="/security">Security</RouterLink> pages.
      </p>
    </article>
  </div>
</template>
